import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ExperienceCard } from './ExperienceCard';
import { ArrowRight } from 'lucide-react';
import { fetchClickStats, rankByClicks } from '../lib/popularExperiences';

/**
 * EventsSection - Homepage row of experiences flagged as `limited_event`.
 * Mirrors the layout/behaviour of ExperiencesSection (horizontal scroll on
 * mobile, 4-col grid on desktop). Shows up to 7 events ranked by total
 * clicks (most clicked first — order preserved, no shuffle), plus a
 * "View all" arrow card that deep-links into the Explore page with the
 * Events filter pre-selected. Renders nothing when there are no limited
 * events, so the homepage stays clean for operators.
 */
export function EventsSection({ experiences }) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [clickStats, setClickStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchClickStats().then((stats) => { if (alive) setClickStats(stats); });
    return () => { alive = false; };
  }, []);

  const events = useMemo(
    () => (experiences || []).filter((e) => e.limited_event),
    [experiences]
  );

  const displayed = useMemo(() => {
    if (clickStats === null) return [];
    return rankByClicks(events, clickStats, { count: 7, shuffle: false });
  }, [events, clickStats]);

  if (events.length === 0) return null;

  return (
    <section
      className="py-[30px] sm:py-[40px]"
      data-testid="events-section"
      id="events"
    >
      <div className="max-w-[1280px] mx-auto px-5 lg:px-6">
        <div className="flex items-center justify-between">
          <h2 className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight">
            {t('events_title')}
          </h2>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto">
        <div className="overflow-x-auto pt-6 pb-6 mt-3 no-scrollbar lg:overflow-visible lg:pt-0 lg:pb-4 lg:mt-7">
          <div className="flex gap-4 snap-x snap-mandatory pl-5 pr-5 lg:grid lg:grid-cols-4 lg:gap-5 lg:snap-none lg:pl-6 lg:pr-6">
            {displayed.map((exp, i) => (
              <ExperienceCard
                key={exp.id}
                exp={exp}
                testIdPrefix="event"
                containerClassName="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0"
                animationDelay={i * 0.05}
              />
            ))}

            {/* Arrow card linking to Explore page with events filter */}
            <div className="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0 rounded-2xl flex flex-col items-center justify-center gap-4">
              <button
                onClick={() => navigate('/Explore?filter=events')}
                data-testid="events-view-all-button"
                className="w-20 h-20 rounded-full bg-[#e11d48] hover:bg-[#be123c] flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <ArrowRight className="w-10 h-10 text-white" strokeWidth={2.5} />
              </button>
              <span
                className="text-[#0F3B63] font-bold text-lg"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {t('view_all_events')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
